// page-actions/state.ts — État mutable de #actions et référentiels dérivés.
//
// Neuvième tranche extraite de `renderActionsPage` (point 16, 2026-09-08).
// La page était une closure de vingt variables `let` ; elles deviennent un
// objet `ActionsPageState` que les modules d'écran lisent et que la page
// seule écrit. Les référentiels immuables (maps, équipes du user) et les deux
// opérations qui en dépendent (filtrer, trier) sont dans `ActionsPageRefs`.
//
// Restaurations à la création :
// - pliage du volet de filtres, mémorisé en localStorage sur mobile seulement
//   (sur desktop le volet doit toujours être ouvert à l'arrivée) ;
// - tri, mémorisé par tenant : revenir de l'éditeur re-dispatch la page, sans
//   ça la liste repartait triée par échéance (bug #7) ;
// - sélection, posée une seule fois avant de naviguer vers l'éditeur
//   (`actions:pendingSelection`) ; un changement de filtre ne la pose pas.
//
// Globaux ambiants : currentUserId, tlsGet, tlsSet, todayStr, trigramHtml.

import { filterActions } from '../../domain/actions-filter.js';
import { sortActions as domainSortActions } from '../../domain/actions-sort.js';
import type { SortState } from './actions-table.js';
import { defaultActionsUrlState, type ActionsUrlState } from './url-state.js';
import type {
  ActionsAction,
  ActionsCommittee,
  ActionsPhase,
  ActionsProgram,
  ActionsProject,
  ActionsStatusFull,
  ActionsStructure,
  ActionsUser,
} from './types.js';

/** État de la page : ce que l'URL persiste (`ActionsUrlState` : filtres, mode
 * d'affichage, seuils, modes réunion) plus ce qui lui est propre. */
export interface ActionsPageState extends ActionsUrlState {
  filtersCollapsed: boolean;
  sort: SortState;
  /** Ids cochés. Même instance pendant toute la vie de la page. */
  readonly selectedIds: Set<number>;
}

const SORT_KEY = 'actions-sort';
const COLLAPSED_KEY = 'act-filters-collapsed';
const PENDING_SELECTION_KEY = 'actions:pendingSelection';

/** Petit écran : le volet de filtres se replie, le CTA « Ajouter » déploie la
 * saisie rapide plutôt que la modale d'édition. */
export function isMobile(): boolean {
  return !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
}

function restoreSort(): SortState {
  const snap = (tlsGet(SORT_KEY) || '').split(':');
  return { key: snap[0] || 'deadline', dir: snap[1] === 'desc' ? 'desc' : 'asc' };
}

function restorePendingSelection(into: Set<number>): void {
  try {
    const snap = sessionStorage.getItem(PENDING_SELECTION_KEY);
    if (!snap) return;
    sessionStorage.removeItem(PENDING_SELECTION_KEY);
    for (const id of JSON.parse(snap) as number[]) into.add(id);
  } catch {
    /* sessionStorage indisponible : la sélection repart à vide */
  }
}

export function createActionsPageState(): ActionsPageState {
  const selectedIds = new Set<number>();
  restorePendingSelection(selectedIds);
  return {
    ...defaultActionsUrlState(),
    filtersCollapsed: isMobile() ? tlsGet(COLLAPSED_KEY) === '1' : false,
    sort: restoreSort(),
    selectedIds,
  };
}

export function persistSort(sort: SortState): void {
  tlsSet(SORT_KEY, `${sort.key}:${sort.dir}`);
}

export function persistFiltersCollapsed(collapsed: boolean): void {
  tlsSet(COLLAPSED_KEY, collapsed ? '1' : '0');
}

export interface ActionsPageRefs {
  users: ActionsUser[];
  statuses: ActionsStatusFull[];
  programs: ActionsProgram[];
  projects: ActionsProject[];
  committees: ActionsCommittee[];
  structures: ActionsStructure[];
  phases: ActionsPhase[];
  userId: number | null;
  userTeamIds: number[];
  userMap: Record<number, ActionsUser>;
  statusMap: Record<number, ActionsStatusFull>;
  projectMap: Record<number, ActionsProject>;
  committeeMap: Record<number, ActionsCommittee>;
  /** Filtre courant appliqué à une liste (domaine : actions-filter). */
  applyFilters: (s: ActionsPageState, actions: ActionsAction[]) => ActionsAction[];
  /** Tri courant appliqué à une liste (domaine : actions-sort). */
  sortActions: (s: ActionsPageState, actions: ActionsAction[]) => ActionsAction[];
}

export interface ActionsPageInput {
  users: unknown[];
  statuses: unknown[];
  programs?: ActionsProgram[];
  projects?: ActionsProject[];
  committees?: ActionsCommittee[];
  structures?: ActionsStructure[];
  phases?: ActionsPhase[];
}

export function createActionsPageRefs(input: ActionsPageInput): ActionsPageRefs {
  const users = input.users as ActionsUser[];
  const statuses = input.statuses as ActionsStatusFull[];
  const programs = input.programs || [];
  const projects = input.projects || [];
  const committees = input.committees || [];
  const structures = input.structures || [];
  const userId = currentUserId();
  const userMap: Record<number, ActionsUser> = Object.fromEntries(users.map((u) => [u.id, u]));
  const statusMap: Record<number, ActionsStatusFull> = Object.fromEntries(
    statuses.map((s) => [s.id, s]),
  );
  const projectMap: Record<number, ActionsProject> = Object.fromEntries(
    projects.map((p) => [p.id, p]),
  );
  const committeeMap: Record<number, ActionsCommittee> = Object.fromEntries(
    committees.map((c) => [c.id, c]),
  );
  // Structure → parents directs, pour le rollup d'escalade : filtrer par une
  // structure parente remonte les actions escaladées de ses filles directes.
  const structParentMap: Record<number, number[]> = Object.fromEntries(
    structures.map((s) => [s.id, s.parent_ids || []]),
  );
  const currentUser = userId != null ? userMap[userId] : null;
  const userTeamIds = currentUser && !currentUser.is_team ? currentUser.team_ids || [] : [];

  // Trigrammes séparés par ', ' : gras conditionnel (équipes) via trigramHtml.
  const nameList = (ids: unknown): string =>
    ((ids as number[] | undefined) || [])
      .map((id) => {
        const u = userMap[id];
        return u ? trigramHtml(u, { fallbackToName: true, placeholder: '?' }) : '?';
      })
      .join(', ') || '—';

  return {
    users,
    statuses,
    programs,
    projects,
    committees,
    structures,
    phases: input.phases || [],
    userId,
    userTeamIds,
    userMap,
    statusMap,
    projectMap,
    committeeMap,
    applyFilters: (s, actions) =>
      filterActions(actions, s.filters, {
        userId,
        userTeamIds,
        statusMap,
        structParentMap,
        programs,
        selectedIds: s.selectedIds,
        todayStr: todayStr(),
      }),
    sortActions: (s, actions) =>
      domainSortActions(actions, s.sort.key, s.sort.dir, {
        nameOf: nameList,
        statusName: (id) => (id != null ? statusMap[id]?.name : '') || '',
        projectCode: (id) => projectMap[id]?.code || '',
        today: todayStr(),
      }),
  };
}
